import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface OnboardingProfile {
  id: string;
  user_id: string;
  experience_level: string;
  goal: string;
  available_days: number;
  disclaimer_accepted: boolean;
  created_at: string;
}

export function useOnboarding(userId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: onboarding, isLoading, isError, refetch } = useQuery({
    queryKey: ["onboarding", userId],
    queryFn: async () => {
      if (!userId) return null;
      const { data, error } = await supabase
        .from("onboarding_profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      return data as OnboardingProfile | null;
    },
    enabled: !!userId,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  });

  const saveOnboarding = useMutation({
    mutationFn: async (profile: {
      experience_level?: string;
      goal?: string;
      available_days?: number;
      disclaimer_accepted: boolean;
    }) => {
      if (!userId) throw new Error("Not authenticated");
      const { error } = await supabase
        .from("onboarding_profiles")
        .upsert({
          user_id: userId,
          ...profile,
        }, { onConflict: "user_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["onboarding", userId] });
    },
  });

  const hasCompletedOnboarding = !!onboarding?.disclaimer_accepted;

  return { onboarding, isLoading, isError, refetch, saveOnboarding, hasCompletedOnboarding };
}
